import { Dialog, DialogContent, IconButton, ImageListImage } from 'omakase';
import { IconArrowLeft, IconArrowRight } from '@tock/icons';
import {
  IconArrowLeft as IconArrowLeftLegacy,
  IconArrowRight as IconArrowRightLegacy,
} from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';

import {
  dialogContent,
  nextIconButton,
  previousIconButton,
  slideContainer,
} from 'omakase/Lightbox/lightboxCssDefs';
import LightboxHeading from 'omakase/Lightbox/LightboxHeading';
import React from 'react';
import Slider, { Settings } from 'react-slick';

export type LightboxImage = ImageListImage;

export interface LightboxProps {
  /**
   * aria-label for the dialog (container)
   */
  'aria-label'?: string;
  /**
   * Open state for the Lightbox dialog
   */
  open: boolean;
  /**
   * onClose function for the Lightbox dialog
   */
  onClose: () => void;
  /**
   * List of images for display in the lightbox
   */
  images: LightboxImage[];
  /**
   * Current image index
   */
  index?: number;
  /**
   * On change image index
   */
  onChangeIndex: (value: number) => void;
}

const Lightbox: React.FC<LightboxProps> = ({
  'aria-label': ariaLabel = 'Image detail lightbox',
  open,
  onClose,
  images,
  index = 0,
  onChangeIndex,
}) => {
  const sliderRef = React.useRef<Slider>(null);

  const handleGoToNext = () => {
    sliderRef.current?.slickNext();
  };

  const handleGoToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        handleGoToPrev();
        break;
      case 'ArrowRight':
        handleGoToNext();
        break;
      case 'Escape':
        onClose();
        break;
      default:
        return;
    }
  };

  const SPEED = 400;

  const settings: Settings = {
    accessibility: true,
    infinite: true,
    initialSlide: index,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    /**
     * Window resize events (easily triggered in iOS Safari 15 with bottom-toolbar) interfere with Slick's afterChange event,
     * we use beforeChange to circumvent this (https://github.com/akiran/react-slick/issues/1262)
     */
    beforeChange: (_, next: number) => setTimeout(() => onChangeIndex(next), SPEED),
    speed: SPEED,
  };

  return (
    <Dialog
      data-testid="image-dialog"
      aria-label={ariaLabel}
      entranceMotion="fadeIn"
      entranceMotionDesktop="fadeIn"
      fullScreen
      open={open}
      onClose={onClose}
      onKeyDown={handleKeyDown}
    >
      <LightboxHeading current={index + 1} total={images.length} onClose={onClose} />
      <DialogContent disablePadding css={dialogContent}>
        {/* Accessible label for screen readers - this is read upon each change in image index */}
        <div className="u-visuallyHidden" aria-live="polite" aria-atomic="true">
          Image {index + 1} of {images.length} - {images[index].alt}
        </div>
        <Slider ref={sliderRef} {...settings}>
          {images.map(({ alt, src, srcSet }) => (
            <div key={alt} css={slideContainer}>
              <img src={src} alt={alt} srcSet={srcSet} />
            </div>
          ))}
        </Slider>
        <IconButton
          css={previousIconButton}
          aria-label="Go to previous image"
          variant="ghostInverted"
          size="medium"
          onClick={handleGoToPrev}
          data-testid="previous-image-arrow"
        >
          <IconMigrationUtil legacyIcon={<IconArrowLeftLegacy />} newIcon={<IconArrowLeft />} />
        </IconButton>
        <IconButton
          css={nextIconButton}
          aria-label="Go to next image"
          variant="ghostInverted"
          size="medium"
          onClick={handleGoToNext}
          data-testid="next-image-arrow"
        >
          <IconMigrationUtil legacyIcon={<IconArrowRightLegacy />} newIcon={<IconArrowRight />} />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
};

export default Lightbox;
